/**
 * Practice on the till (`/v1/practice`): staff learn on the real menu and
 * tables, and nothing they do counts.
 *
 * A practising till works in a session of its own. Its tokens say so
 * (`prac` in the access token), and the server keeps everything it does
 * apart from the real restaurant and throws it away when practice ends. The
 * till's real sign-in is kept aside meanwhile (`PracticeSwitch`).
 */

import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError } from './client';
import { useApi, usePracticeSwitch, useSession } from './provider';

export interface PracticeStarted {
  session_id: string;
  until: string;
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  refresh_expires_in: number;
}

export interface Practice {
  practising: boolean;
  /** When practice ends whatever happens, a day after it started. */
  until: Date | null;
}

const NOT_PRACTISING: Practice = { practising: false, until: null };

/** What an access token says about practice. Read on the device: offline too. */
export function practiceOf(accessToken: string | null | undefined): Practice {
  if (!accessToken) return NOT_PRACTISING;
  try {
    const part = accessToken.split('.')[1] ?? '';
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      prac?: unknown;
      pracu?: unknown;
    };
    if (typeof claims.prac !== 'string') return NOT_PRACTISING;
    return {
      practising: true,
      until: typeof claims.pracu === 'number' ? new Date(claims.pracu * 1000) : null,
    };
  } catch {
    return NOT_PRACTISING;
  }
}

/** Whether this till is practising now. */
export function usePractice(): Practice {
  const api = useApi();
  const { epoch } = useSession();
  // `epoch` moves whenever the session's tokens are swapped, which is the
  // only time the answer can change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => practiceOf(api.tokens.get()?.access), [api, epoch]);
}

/** Put this till into practice. A manager's (`practice.session.manage`). */
export function useStartPractice() {
  const api = useApi();
  const practice = usePracticeSwitch();
  return useMutation<PracticeStarted, ApiError, { locationId: string }>({
    mutationFn: ({ locationId }) =>
      api.post<PracticeStarted>('/v1/practice', { location_id: locationId }),
    onSuccess: (started) =>
      practice.enter({
        access: started.access_token,
        refresh: started.refresh_token,
        expiresAt: Date.now() + started.expires_in * 1000,
      }),
  });
}

/**
 * End practice on this till: the server throws away everything it did, and
 * the till goes back to its own sign-in. Back even if the server could not be
 * told (practice already ran out): what practice did is thrown away by the
 * server a day after it began in any case.
 */
export function useEndPractice() {
  const api = useApi();
  const practice = usePracticeSwitch();
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      try {
        await api.post<void>('/v1/practice/end', { refresh_token: practice.practiceRefresh() });
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) throw error;
      }
    },
    onSuccess: () => practice.leave(),
  });
}
